/**
 * Stage A — interaction state for one surface's timer acts.
 *
 *   idle → collecting_input → submitting → idle
 *
 * NOT a timer state: it only says what the SURFACE is doing (asking the
 * technician something, or waiting on the server). Its job is to make a second
 * submission impossible while one is in flight — double Start, Resume spam,
 * a Stop clicked twice — and to make cancellation predictable: cancelling an
 * input step clears every transient fact so nothing leaks into a later act.
 *
 * Pure and synchronous (a gate is checked and taken in the same tick, which is
 * what makes a double click harmless). Presentation-agnostic.
 */
import type { TimerActionIntent } from './workState';
import type { TimerInputStep } from './actionPlan';

export type TimerInteractionPhase = 'idle' | 'collecting_input' | 'submitting';

export interface TimerInteractionState<Facts> {
  phase: TimerInteractionPhase;
  intent: TimerActionIntent | null;
  /** Remaining input steps; the head is the one on screen. */
  steps: readonly TimerInputStep[];
  /** Human facts collected so far for THIS act. */
  facts: Partial<Facts>;
}

export function idleInteraction<Facts>(): TimerInteractionState<Facts> {
  return { phase: 'idle', intent: null, steps: [], facts: {} };
}

export class TimerInteraction<Facts extends object> {
  private state: TimerInteractionState<Facts> = idleInteraction<Facts>();

  get current(): TimerInteractionState<Facts> { return this.state; }
  get busy(): boolean { return this.state.phase === 'submitting'; }
  get step(): TimerInputStep | null { return this.state.steps[0] ?? null; }

  /**
   * Begin an act. Refused (returns false) while a submission is in flight, so a
   * second click cannot start a second act on top of the first. Beginning while
   * collecting replaces the abandoned act (its facts are dropped).
   */
  begin(intent: TimerActionIntent, steps: readonly TimerInputStep[], seed: Partial<Facts> = {}): boolean {
    if (this.busy) return false;
    this.state = { phase: steps.length ? 'collecting_input' : 'idle', intent, steps, facts: { ...seed } };
    return true;
  }

  /** The head step was answered; move to the next. Returns the next step or null when ready to submit. */
  complete(step: TimerInputStep, facts: Partial<Facts> = {}): TimerInputStep | null {
    if (this.state.intent === null || this.state.steps[0] !== step) return this.step;
    const steps = this.state.steps.slice(1);
    this.state = { ...this.state, steps, facts: { ...this.state.facts, ...facts },
      phase: steps.length ? 'collecting_input' : this.state.phase };
    return this.step;
  }

  /**
   * Record a fact the server taught THIS act (e.g. a bay it refused), so the
   * next input step can use it. Forgotten with the act on settle / cancel.
   */
  remember(facts: Partial<Facts>): void {
    if (this.state.intent === null) return;
    this.state = { ...this.state, facts: { ...this.state.facts, ...facts } };
  }

  /** Re-ask a step (e.g. the server refused the chosen bay), keeping the other facts. */
  reask(step: TimerInputStep, clear: (keyof Facts)[] = []): void {
    if (this.state.intent === null) return;
    const facts = { ...this.state.facts };
    for (const k of clear) delete facts[k];
    this.state = { ...this.state, phase: 'collecting_input', steps: [step, ...this.state.steps], facts };
  }

  /**
   * Take the submission gate. Returns the collected facts, or null if a
   * submission is already in flight (the caller must not submit).
   */
  submit(): Partial<Facts> | null {
    if (this.busy || this.state.intent === null) return null;
    this.state = { ...this.state, phase: 'submitting', steps: [] };
    return this.state.facts;
  }

  /** The server answered. Keep the facts only if the caller will retry the SAME act. */
  settle({ keepFacts = false }: { keepFacts?: boolean } = {}): void {
    this.state = keepFacts
      ? { ...this.state, phase: 'idle', steps: [] }
      : idleInteraction<Facts>();
  }

  /** The technician walked away. Nothing was submitted; nothing is remembered. */
  cancel(): void {
    if (this.busy) return;          // a request in flight cannot be un-sent
    this.state = idleInteraction<Facts>();
  }
}
